/**
 * 订阅生命周期管理组件
 *
 * 职责：处理用户登录时的完整订阅生命周期状态检查和处理
 * - 新用户初始化
 * - 付费订阅到期续费提示
 * - 付费订阅到期懒加载降级
 * - Free订阅到期自动续订
 */

import React, { useEffect, useCallback, useState } from 'react';
import { useUser } from '@readsavor/auth';
import { useSubscriptionRenewal, useSubscriptionReset } from '@readsavor/integration';
import { useSubscriptionState, useSubscriptionCycle } from '@readsavor/subscription';
import type { SubscriptionPlanType, SubscriptionCycleResponse } from '@readsavor/types';
import { Toast, ToastType } from '@readsavor/ui';

interface SubscriptionLifecycleManagerProps {
  children: React.ReactNode;
}

/**
 * 订阅生命周期管理组件
 *
 * 使用正确的新架构模式：
 * - 使用集成包的useUserInitialization Hook
 * - 调用集成域进行完整的用户初始化流程
 * - 处理完整的订阅生命周期场景
 * - 统一的会话缓存和错误处理
 */
export const SubscriptionLifecycleManager: React.FC<SubscriptionLifecycleManagerProps> = ({
  children,
}) => {
  const { data: user, isLoading: isUserLoading } = useUser();
  const { mutateAsync: renewSubscription } = useSubscriptionRenewal();
  const { mutateAsync: resetToFree } = useSubscriptionReset();

  // ✅ 使用订阅包的Hooks获取当前订阅状态和周期
  const { data: subscriptionState, isLoading: isSubscriptionStateLoading } = useSubscriptionState();
  const { data: cycle, isLoading: isCycleLoading } = useSubscriptionCycle();
  const plan = subscriptionState?.plan;

  // 聚合所有数据加载状态
  const isDataLoading = isUserLoading || isSubscriptionStateLoading || isCycleLoading;

  // 生命周期管理状态
  const [lifecycleState, setLifecycleState] = useState({
    isProcessing: false,
    isCompleted: false,
    error: null as string | null,
  });

  // Toast 状态管理
  const [toastState, setToastState] = useState<{
    visible: boolean;
    message: string;
    type: ToastType;
  }>({
    visible: false,
    message: '',
    type: 'default',
  });

  // ============================================================================
  // 会话级处理完成标记管理
  // ============================================================================

  const getLifecycleCacheKey = useCallback(
    (userId: string): string => `subscription_lifecycle_done_${userId}`,
    []
  );
  const LIFECYCLE_CACHE_VALUE = 'completed';

  const hasLifecycleCompletionMark = useCallback(
    (userId: string): boolean => {
      try {
        return sessionStorage.getItem(getLifecycleCacheKey(userId)) === LIFECYCLE_CACHE_VALUE;
      } catch {
        return false;
      }
    },
    [getLifecycleCacheKey]
  );

  // ============================================================================
  // 订阅生命周期处理逻辑
  // ============================================================================

  const handleSubscriptionLifecycle = useCallback(
    async (
      userId: string,
      currentPlan: SubscriptionPlanType,
      currentCycle: SubscriptionCycleResponse | null | undefined
    ) => {
      // 会话级缓存检查 - 如果本次会话已完成处理，直接跳过
      if (hasLifecycleCompletionMark(userId)) {
        setLifecycleState({
          isProcessing: false,
          isCompleted: true,
          error: null,
        });
        return;
      }

      // 防止重复调用
      if (lifecycleState.isProcessing) {
        return;
      }

      setLifecycleState({
        isProcessing: true,
        isCompleted: false,
        error: null,
      });

      try {
        // ============================================================================
        // ============================================================================
        // 场景2: 付费订阅到期处理 (降级) 💳⬇️
        // ============================================================================
        const isExpired = currentCycle && new Date(currentCycle.effectiveEndDate) < new Date();
        if (isExpired && currentCycle && currentPlan !== 'free') {
          // 在新架构中，续费由后端 webhook 处理。
          // 如果前端检测到付费订阅已过期，意味着后端续费已失败，因此前端仅负责执行降级。
          await resetToFree({});
          setToastState({
            visible: true,
            message:
              'Your paid plan has expired and your account has been switched to the free plan.',
            type: 'info',
          });
        }

        // ============================================================================
        // 场景4: Free订阅到期 - 自动续订Free 🔄
        // ============================================================================
        if (isExpired && currentCycle && currentPlan === 'free') {
          try {
            const idempotencyKey = `${currentCycle.startDate}_${currentCycle.effectiveEndDate}`;
            await renewSubscription({ idempotencyKey });
            // 免费计划续订成功后，静默处理，不打扰用户
          } catch (freeRenewalError) {
            // 如果免费续订失败，这是一个需要关注的系统级问题，但我们不应打扰用户
            console.error(
              `[SubscriptionLifecycle] Free subscription auto-renewal failed. This is a critical issue that needs backend investigation.`,
              freeRenewalError
            );
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error(
          `[SubscriptionLifecycle] Subscription lifecycle processing failed:`,
          errorMessage
        );
        setToastState({
          visible: true,
          message: `Account initialization failed: ${errorMessage}`,
          type: 'error',
        });

        setLifecycleState({
          isProcessing: false,
          isCompleted: false,
          error: errorMessage,
        });
      }
    },
    [hasLifecycleCompletionMark, lifecycleState.isProcessing, renewSubscription, resetToFree]
  );

  // 用户变化时触发生命周期检查
  useEffect(() => {
    // 等待所有数据加载完成
    if (isDataLoading) return;

    if (user?.id && plan) {
      handleSubscriptionLifecycle(user.id, plan, cycle);
    } else if (!user?.id) {
      // 用户登出时重置状态
      setLifecycleState({
        isProcessing: false,
        isCompleted: false,
        error: null,
      });
    }
  }, [user?.id, plan, cycle, isDataLoading, handleSubscriptionLifecycle]);

  return (
    <>
      {children}
      {toastState.visible && (
        <Toast
          type={toastState.type}
          visible={toastState.visible}
          onClose={() => setToastState({ ...toastState, visible: false })}
          autoClose={true}
          duration={5000}
          title={toastState.type === 'success' ? 'Success' : 'An Error Occurred'}
        >
          {toastState.message}
        </Toast>
      )}
    </>
  );
};
